import type { Depense, GroupeDepenses } from '../../types/ndf';
import { BoutonPrincipal, dateFr } from './ui/Champ';
import { euros } from '../../pages/ndf/NdfMesNotesPage';

/**
 * Sélection des dépenses à ranger dans la note, groupe par groupe.
 *
 * Cocher l'en-tête d'un groupe prend toutes ses dépenses : douze dépenses
 * réparties sur deux missions et un lot de non refacturables se rangent en
 * trois gestes.
 */
export function PanneauAssociation({
  groupes,
  depenses,
  choisies,
  onBasculer,
  enCours,
  onValider,
  masquerValidation = false,
}: {
  groupes: GroupeDepenses[];
  depenses: Depense[];
  choisies: Set<string>;
  onBasculer: (ids: string[], actif: boolean) => void;
  enCours: boolean;
  onValider: () => void;
  /** L'appelant gère lui-même la validation (choix de la note de destination). */
  masquerValidation?: boolean;
}) {
  if (groupes.length === 0) {
    return (
      <section className="rounded-lg border border-nuit/10 bg-white p-4">
        <p className="text-sm text-nuit/70">
          Toutes vos dépenses en brouillon sont déjà rattachées à une note.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-3 rounded-lg border border-ciel bg-white p-4">
      <h2 className="font-titre text-sm font-bold text-nuit">Dépenses à ajouter</h2>

      {groupes.map((g) => {
        const toutes = g.depense_ids.every((i) => choisies.has(i));
        return (
          <div key={g.cle} className="rounded-md border border-nuit/10 p-3">
            <label className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                checked={toutes}
                onChange={(e) => onBasculer(g.depense_ids, e.target.checked)}
                className="h-4 w-4 rounded border-nuit/30 text-nuit focus:ring-ciel"
              />
              <span className="font-titre text-sm font-bold text-nuit">
                {g.libelle}
                <span className="ml-1 font-sans font-normal text-nuit/60">
                  ({g.depense_ids.length})
                </span>
              </span>
            </label>

            <ul className="mt-1 space-y-1 pl-6">
              {g.depense_ids.map((did) => {
                const d = depenses.find((x) => x.id === did);
                if (!d) return null;
                return (
                  <li key={did}>
                    <label className="flex min-h-11 items-center justify-between gap-3 text-sm">
                      <span className="flex min-w-0 items-center gap-2">
                        <input
                          type="checkbox"
                          checked={choisies.has(did)}
                          onChange={(e) => onBasculer([did], e.target.checked)}
                          className="h-4 w-4 shrink-0 rounded border-nuit/30 text-nuit focus:ring-ciel"
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-nuit">
                            {d.description || 'Dépense'}
                          </span>
                          <span className="block text-nuit/60">{dateFr(d.date_depense)}</span>
                        </span>
                      </span>
                      <span className="shrink-0 font-medium text-nuit">{euros(d.montant_ttc)}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}

      {!masquerValidation && (
        <BoutonPrincipal
          type="button"
          disabled={choisies.size === 0 || enCours}
          onClick={onValider}
          className="w-full"
        >
          {enCours
            ? 'Ajout…'
            : `Ajouter ${choisies.size} dépense${choisies.size > 1 ? 's' : ''} à la note`}
        </BoutonPrincipal>
      )}
    </section>
  );
}
